import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheckCircle, FiShield, FiTrendingUp, FiZap } from 'react-icons/fi';

const pillars = [
  {
    icon: FiTrendingUp,
    title: 'Multi-timeframe analysis',
    description: 'The bot works across M5, M15, H1, H4, and D1 so entries are checked against broader market structure before execution.',
  },
  {
    icon: FiShield,
    title: 'Model and indicator filtering',
    description: 'Signal generation is supported by model prediction and indicator validation using ATR, ADX, RSI, MACD, support and resistance, and related technical inputs.',
  },
  {
    icon: FiZap,
    title: 'Execution with guardrails',
    description: 'The live workflow supports market and pending orders, max-trade and max-pending controls, daily thresholds, and MT5-linked monitoring.',
  },
];

const capabilityGroups = [
  {
    title: 'Analysis stack',
    items: [
      'Five active timeframes: M5, M15, H1, H4, and D1.',
      'Indicator preprocessing includes ATR, ADX, RSI, MACD, support, resistance, and momentum-based inputs.',
      'The system uses higher-level analysis before entry rather than relying on a single trigger.',
    ],
  },
  {
    title: 'Signal and execution flow',
    items: [
      'Model-guided prediction is used for BTCUSDm and XAUUSDm in the current code path.',
      'Execution can work with open positions and pending orders, with explicit max-trade and max-pending limits.',
      'Trading behavior can be adjusted through Auto mode, weekday and weekend modes, and ADX threshold settings.',
    ],
  },
  {
    title: 'Risk and control layer',
    items: [
      'Risk per trade is sized around a 1% account-risk model.',
      'Initial stop loss and take profit are derived from ATR-based multipliers.',
      'Users can configure daily profit and loss thresholds, symbol lists, trade caps, and optional hedging behavior.',
    ],
  },
];

const values = [
  'Automation is constrained by user-defined limits rather than left fully open-ended.',
  'Execution decisions are filtered through higher-timeframe context before entry.',
  'Risk controls remain visible through trade caps, thresholds, and configurable operating modes.',
  'MT5 stays part of the workflow so monitoring and intervention remain practical.',
  'Future enhancements are aimed at tighter oversight, not added complexity for its own sake.',
];

const About = () => {
  return (
    <div className="min-h-screen overflow-hidden bg-[#07111a] text-white">
      <div className="fixed inset-0 -z-20 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.16),_transparent_28%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.16),_transparent_30%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />

      <section className="px-4 pb-16 pt-32 sm:px-6 lg:px-8 lg:pt-36">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-200">
                <FiCheckCircle className="h-4 w-4" />
                Built from personal trading experience
              </div>
              <h1 className="mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
                About the product,
                <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
                  and why it exists
                </span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
                DailyProfit Trader was built from personal experience with overtrading, emotional decisions, and the pressure of constant chart watching. The product is designed to bring more structure to execution while keeping the trader in control through clear limits and configurable rules.
              </p>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
              <p className="text-sm uppercase tracking-[0.24em] text-cyan-200/70">Platform focus</p>
              <div className="mt-5 space-y-4 text-sm leading-7 text-slate-300">
                <p>The platform is intended for traders who want a more structured workflow instead of constant manual reaction.</p>
                <p>It combines automated monitoring with user-defined limits so execution can stay controlled without becoming a black box.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl lg:p-8">
          <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
            <div className="rounded-[1.75rem] border border-white/10 bg-slate-950/65 p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-200/70">Why it matters</p>
              <h2 className="mt-4 text-3xl font-black text-white">The project started as a response to inconsistent execution</h2>
              <p className="mt-4 leading-8 text-slate-300">
                It was shaped by the experience of trading too frequently, checking positions too often, and letting short-term pressure override process. The aim is to make execution more deliberate by defining limits in advance and letting the system handle routine monitoring.
              </p>
            </div>

            <div className="rounded-[1.75rem] border border-white/10 bg-slate-950/65 p-6">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200/70">The product response</p>
              <h2 className="mt-4 text-3xl font-black text-white">The bot adds controls that support more disciplined execution</h2>
              <p className="mt-4 leading-8 text-slate-300">
                The current platform supports configurable symbols, maximum open and pending trades, daily profit and loss thresholds, weekday and weekend trade modes, ADX-based operating thresholds, optional hedging, and account-level monitoring around an MT5-connected workflow. It also combines model-supported signal generation with indicator-based analysis before execution so entries are filtered through broader market conditions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200/70">Platform capabilities</p>
            <h2 className="mt-4 text-4xl font-black text-white">Core capabilities in the current build</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {pillars.map((pillar) => {
              const Icon = pillar.icon;

              return (
                <div key={pillar.title} className="rounded-[1.75rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
                  <div className="inline-flex rounded-2xl border border-cyan-300/20 bg-cyan-300/10 p-3 text-cyan-200">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-2xl font-bold text-white">{pillar.title}</h3>
                  <p className="mt-3 leading-7 text-slate-400">{pillar.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl grid gap-6 lg:grid-cols-3">
          {capabilityGroups.map((group) => (
            <div key={group.title} className="rounded-[1.75rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
              <h3 className="text-2xl font-bold text-white">{group.title}</h3>
              <div className="mt-5 space-y-3">
                {group.items.map((item) => (
                  <div key={item} className="flex items-start gap-3 text-slate-300">
                    <FiCheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" />
                    <span className="leading-7">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-white/10 bg-[linear-gradient(135deg,rgba(16,185,129,0.08),rgba(14,165,233,0.03),rgba(255,255,255,0.02))] p-6 backdrop-blur-xl lg:p-8">
          <div className="grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-200/70">Operating principles</p>
              <h2 className="mt-4 text-4xl font-black text-white">How the workflow is meant to behave</h2>
            </div>
            <div className="space-y-4">
              {values.map((value) => (
                <div key={value} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-slate-950/60 p-4 text-slate-300">
                  <FiCheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-300" />
                  <span>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl lg:p-8">
          <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200/70">Next enhancement</p>
              <h2 className="mt-4 text-4xl font-black text-white">OpenCLAW is planned to strengthen oversight and control</h2>
            </div>
            <div className="space-y-4 text-slate-300">
              <p>
                The roadmap includes introducing OpenCLAW as an additional control layer to improve how execution is supervised, constrained, and adjusted during live operation.
              </p>
              <p>
                That direction fits the core purpose of the product: helping traders reduce impulsive behavior while keeping stronger control over how the bot responds to changing market conditions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-24 pt-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl rounded-[2.5rem] border border-white/10 bg-[linear-gradient(135deg,#0f1722_0%,#0f2c33_55%,#093340_100%)] p-8 shadow-[0_30px_100px_rgba(0,0,0,0.35)] lg:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-cyan-200/70">Next step</p>
              <h2 className="mt-4 text-4xl font-black text-white sm:text-5xl">Create an account and see whether the workflow matches the discipline you want to keep.</h2>
              <p className="mt-4 max-w-2xl text-lg leading-8 text-slate-300">
                Set up the account, connect MT5, review the controls, and decide whether the platform fits how you want to manage risk and execution.
              </p>
            </div>

            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-7 py-4 font-bold text-slate-950 transition hover:scale-[1.02]"
            >
              Create account
              <FiArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
